import React, { useState } from 'react';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import { DataType, TimeSlot, SlotStatus } from '@/types/doctorManagementTypes';
import { BookedAppointment } from './BookedAppointment';
import { CancelAndReschedule } from './CancelAndReschedule';
import styles from './style.module.css'; // Import the CSS Module

interface WeekSchedulerProps {
  onLeftArrowClick: () => void;
  onRightArrowClick: () => void;
  data: DataType;
  onSlotClick: () => void;
}

const formatDateRange = (startDate: string): string => {
  const start = new Date(startDate);

  const end = new Date(start);
  end.setDate(start.getDate() + 6);

  const startDay = String(start.getDate()).padStart(2, '0');
  const endDay = String(end.getDate()).padStart(2, '0');
  const month = start.toLocaleString('default', { month: 'long' });
  const year = start.getFullYear();

  return `${startDay} - ${endDay} ${month} ${year}`;
};

const getDayOfWeek = (date: Date): string => {
  const days = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];
  return days[date.getDay()];
};

export const WeekScheduler: React.FC<WeekSchedulerProps> = ({
  onLeftArrowClick,
  onRightArrowClick,
  data: { days: initialDays, weekStartDate },
  onSlotClick,
}) => {
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [isBookedModalOpen, setIsBookedModalOpen] = useState<boolean>(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false);

  const weekStart = new Date(weekStartDate);
  const days = Array.from({ length: 7 }, (_, i) => {
    const day = new Date(weekStart);
    day.setDate(weekStart.getDate() + i);
    return {
      dayOfWeek: getDayOfWeek(day),
      date: day.toISOString().split('T')[0],
      isAvailable: initialDays[i].isAvailable,
      timeSlots: initialDays[i].timeSlots,
    };
  });

  const handleSlotClick = (slot: TimeSlot) => {
    if (slot.status === 'Booked') {
      setSelectedSlot(slot);
      setIsBookedModalOpen(true);
    } else if (slot.status === 'Available') {
      onSlotClick();
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedSlot(null);
  };

  const handleCancelAndReschedule = () => {
    setIsBookedModalOpen(false);
    setIsCancelModalOpen(true);
  };

  const handleSlotUpdate = (newStatus: SlotStatus) => {
    if (selectedSlot) {
      selectedSlot.status = newStatus;
      handleModalClose();
    }
  };

  return (
    <div className={styles['week-scheduler']}>
      <h2 className={styles['week-scheduler-calendar-title']}>
        <ChevronLeft
          style={{ width: '20px', height: '20px' }}
          onClick={onLeftArrowClick}
        />
        {formatDateRange(weekStartDate)}
        <ChevronRight
          style={{ width: '20px', height: '20px' }}
          onClick={onRightArrowClick}
        />
      </h2>
      <div className="max-h-[420px] overflow-y-auto overflow-x-hidden">
        <table className="w-full border-collapse text-center">
          <thead>
            <tr>
              <th className="bg-[#fff] text-left">Time Slots</th>
              {days.map((day, index) => (
                <th
                  key={index}
                  style={{
                    backgroundColor: day.isAvailable ? '#F0F1FF' : '#F3F3F4',
                  }}
                >
                  {day.dayOfWeek}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {days[0].timeSlots.map((slot, slotIndex) => (
              <tr key={slotIndex} className="bg-[#fff] text-left">
                <td>{slot.time}</td>
                {days.map((day, dayIndex) => (
                  <td
                    key={dayIndex}
                    style={{
                      backgroundColor:
                        !day.isAvailable ||
                        day.timeSlots[slotIndex].status === 'Unavailable'
                          ? '#F3F3F4'
                          : '#F0F1FF',
                      cursor: day.isAvailable ? 'pointer' : 'not-allowed',
                    }}
                    onClick={() =>
                      day.isAvailable &&
                      handleSlotClick(day.timeSlots[slotIndex])
                    }
                  >
                    {day.isAvailable && (
                      <>
                        {day.timeSlots[slotIndex].status === 'Booked' && (
                          <span className={`${styles['status']} rounded-[4px]`}>
                            <div className="bg-primary w-[10px] h-[10px] mr-[8px]"></div>
                            Booked
                          </span>
                        )}
                        {day.timeSlots[slotIndex].status === 'Available' && (
                          <span className={`${styles['status']} rounded-[4px]`}>
                            <div className="bg-[#27cdaf] w-[10px] h-[10px] mr-[8px]"></div>
                            Available
                          </span>
                        )}
                        {!['Booked', 'Available'].includes(
                          day.timeSlots[slotIndex].status
                        ) && <span style={{ color: '#999' }}></span>}
                      </>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal for Slot Update */}
      {isModalOpen && selectedSlot && (
        <div className="modal">
          <h3>Update Slot: {selectedSlot.time}</h3>
          <button onClick={() => handleSlotUpdate('Available')}>
            Set as Available
          </button>
          <button onClick={() => handleSlotUpdate('Booked')}>
            Set as Booked
          </button>
          <button onClick={() => handleSlotUpdate('Unavailable')}>
            Set as Unavailable
          </button>
          <button onClick={handleModalClose}>Close</button>
        </div>
      )}

      {/* BookedAppointment modal */}
      {isBookedModalOpen && selectedSlot && (
        <BookedAppointment
          open={isBookedModalOpen}
          onOpenChange={setIsBookedModalOpen}
          handleCancelAndReschedule={handleCancelAndReschedule}
        />
      )}

      {/* CancelAndReschedule modal */}
      {isCancelModalOpen && selectedSlot && (
        <CancelAndReschedule
          open={isCancelModalOpen}
          onOpenChange={setIsCancelModalOpen}
        />
      )}
    </div>
  );
};
